export class ApiError extends Error {
  /** True when the server refused because the session is gone, not because the
   * request was wrong. Components use it to say "sign in again" rather than
   * "failed to load X" — the misleading-message bug that cost a volunteer
   * coordinator a morning on 2026-09-22. */
  public sessionExpired: boolean;

  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.sessionExpired = status === 401;
  }
}

/**
 * Turn any thrown error into a message safe to show a volunteer.
 *
 * ApiError carries the raw response body in `.message`. For FastAPI that is
 * usually `{"detail":"..."}` (a string) or, for 422s, `{"detail":[{...,"msg"}]}`
 * (an array). Rendering that raw leaks `{"detail":...}` or `[object Object]`
 * onto the screen. This extracts the human part, or falls back to a generic
 * line. Messages that are already human (e.g. our 401 copy) pass through.
 *
 * A body can also come from something in front of the API, not FastAPI
 * itself, e.g. a reverse proxy's 413 page. That is markup, not JSON, so it
 * falls to the generic message rather than rendering HTML into the page.
 */
export function humanizeError(
  e: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  const raw = e instanceof Error ? e.message : typeof e === "string" ? e : "";
  const trimmed = raw.trim();
  if (!trimmed) return fallback;
  if (trimmed.startsWith("<")) return fallback;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      const detail = parsed?.detail ?? parsed;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
      return fallback;
    } catch {
      return fallback;
    }
  }
  return trimmed;
}

export interface User {
  id: string;
  username: string;
  role: "admin" | "volunteer";
  display_name: string | null;
}

async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  return fetch(input, { credentials: "include", ...init });
}

/**
 * The session is gone: drop the stale user (so route guards and the account
 * menu stop showing someone as signed in while every call is refused) and
 * bounce to the sign-in screen. Exported so the few call sites that need the
 * raw Response, or that stash unsaved work first (RecordContact), get the same
 * behaviour. Imported lazily: stores/auth imports this module, and a static
 * import here would close the cycle.
 */
export const SESSION_EXPIRED_FLAG = "canvass.sessionExpired";

export async function handleSessionExpired(): Promise<void> {
  // One-shot flag the sign-in page reads, so the volunteer is told WHY they
  // were sent there (codexms review #1805). Storage can be blocked; the
  // redirect must still happen, so failures are ignored.
  try { sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1"); } catch { /* storage blocked */ }
  const { clearUser } = await import("./stores/auth");
  clearUser();
  const { push } = await import("svelte-spa-router");
  push("/login");
}

export const SESSION_EXPIRED_MESSAGE = "Your session expired. Please sign in again.";

/**
 * fetch() for authenticated endpoints. Returns the Response for non-401s
 * (callers still check res.ok), but a 401 always runs handleSessionExpired()
 * and throws ApiError(401). Use this, not bare fetch(), for any call that needs
 * a signed-in user, so no call site can miss the session-expiry bounce.
 */
export async function apiFetchAuthed(input: string, init?: RequestInit): Promise<Response> {
  const res = await apiFetch(input, init);
  if (res.status === 401) {
    await handleSessionExpired();
    throw new ApiError(401, SESSION_EXPIRED_MESSAGE);
  }
  return res;
}

async function apiFetchJson<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await apiFetchAuthed(input, init);
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

export async function getMe(): Promise<User | null> {
  const res = await apiFetch("/api/me");
  if (res.status === 401) return null;
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<User>;
}

export async function login(username: string, password: string): Promise<User> {
  const res = await apiFetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<User>;
}

export async function logout(): Promise<void> {
  await apiFetch("/api/auth/logout", { method: "POST" });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await apiFetch("/api/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export async function setup(
  username: string,
  password: string,
  display_name?: string,
): Promise<User> {
  const res = await apiFetch("/api/auth/setup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, display_name: display_name ?? null }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<User>;
}

export async function signup(
  token: string,
  username: string,
  password: string,
  display_name?: string,
): Promise<User> {
  const res = await apiFetch(`/api/auth/signup/${token}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, display_name: display_name ?? null }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<User>;
}

export async function resetPassword(
  token: string,
  password: string,
): Promise<User> {
  const res = await apiFetch(`/api/auth/reset/${token}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<User>;
}

// ── Types re-exported for convenience ────────────────────────────────────────

export type { VoterSummary } from './stores/voters';

// ── Voter list ────────────────────────────────────────────────────────────────

import type { Filters } from './filters';
import { filtersToParams } from './filters';
import type { PolygonVertex } from './stores/polygon';

export interface VoterListResponse {
  items: import('./stores/voters').VoterSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface VoterFacets {
  precincts: string[];
  // gender options are hardcoded in FilterDrawer.svelte (F/M/U need readable
  // labels), so the backend doesn't compute or return a genders facet at all.
}

export async function getVoterFacets(): Promise<VoterFacets> {
  return apiFetchJson<VoterFacets>('/api/voters/facets');
}

export async function listVoters(
  f: Filters,
  limit = 100,
  offset = 0,
  audience: 'targeted' | 'all' = 'targeted',
): Promise<VoterListResponse> {
  const p = filtersToParams(f);
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  // The backend defaults to targeted and honours 'all' for admins only.
  if (audience === 'all') p.set('audience', 'all');
  return apiFetchJson<VoterListResponse>(`/api/voters?${p}`);
}

// ── My contacts ───────────────────────────────────────────────────────────────

export interface MyContactItem {
  id: string;
  voter_id: string;
  voter_first_name: string;
  voter_last_name: string;
  voter_address: string;
  contact_method: string;
  contact_result: string;
  support_level: number | null;
  note: string | null;
  created_at: string;
}

export async function getMyContacts(since?: string): Promise<MyContactItem[]> {
  let url = '/api/contacts?user_id=me';
  if (since) url += `&since=${encodeURIComponent(since)}`;
  return apiFetchJson<MyContactItem[]>(url);
}

// ── Voter contact history ────────────────────────────────────────────────────

export interface ContactHistoryUser {
  id: string;
  username: string;
  display_name: string | null;
}

export interface ContactHistoryItem {
  id: string;
  voter_id: string;
  contact_method: string;
  contact_result: string;
  support_level: number | null;
  yard_sign_requested: boolean | null;
  volunteer_interest: boolean | null;
  donation_interest: boolean | null;
  donated: boolean | null;
  volunteered: boolean | null;
  callback_at: string | null;
  note: string | null;
  issue_other: string | null;
  created_at: string;
  user: ContactHistoryUser;
  issue_tags: string[];
}

export async function getVoterHistory(voterId: string): Promise<ContactHistoryItem[]> {
  const resp = await apiFetchJson<{ items: ContactHistoryItem[] }>(
    `/api/contacts/by-voter/${voterId}`,
  );
  return resp.items;
}

export async function deleteContact(contactId: string): Promise<void> {
  const res = await apiFetchAuthed(`/api/contacts/${contactId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch {}
    throw new ApiError(res.status, body || `DELETE failed: ${res.status}`);
  }
}

// ── Voter edit ────────────────────────────────────────────────────────────────

export interface VoterPatch {
  phone?: string | null;
  age?: number | null;
  email?: string | null;
  do_not_contact?: boolean;
}

export async function getVoter(
  id: string,
): Promise<import('./stores/voters').VoterSummary> {
  return apiFetchJson<import('./stores/voters').VoterSummary>(`/api/voters/${id}`);
}

export async function patchVoter(
  id: string,
  patch: VoterPatch,
): Promise<import('./stores/voters').VoterSummary> {
  return apiFetchJson<import('./stores/voters').VoterSummary>(`/api/voters/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export async function setVoterTargeting(
  voterId: string,
  targeted: boolean,
): Promise<import('./stores/voters').VoterSummary> {
  const res = await apiFetchAuthed(`/api/voters/${voterId}/targeting`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targeted }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<import('./stores/voters').VoterSummary>;
}

// ── Admin status ──────────────────────────────────────────────────────────────

export interface AdminSyncRun {
  started_at: string | null;
  finished_at: string | null;
  status: string;
  voters_added: number;
  voters_removed: number;
  voters_updated: number;
  rejected_rows_count: number;
  error_message: string | null;
}

export interface AdminBackup {
  finished_at: string;
  size_bytes: number | null;
  remote_path: string | null;
}

export interface AdminLogin {
  username: string;
  last_used_at: string;
  ip: string | null;
}

export interface AdminStatus {
  sync_runs: AdminSyncRun[];
  geocode_queue: number;
  totals: { voters: number; contacts: number; users: number };
  last_logins: AdminLogin[];
  last_backup: AdminBackup | null;
  last_ballot_import?: {
    finished_at: string | null;
    source: string;
    matched_count: number;
    voted_count: number;
  } | null;
}

export async function adminStatus(): Promise<AdminStatus> {
  return apiFetchJson<AdminStatus>('/api/admin/status');
}

// ── Admin: NCSBE sync runs ───────────────────────────────────────────────────

export interface NCSBESyncRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'ok' | 'error';
  voters_added: number;
  voters_updated: number;
  voters_inactivated: number;
  voters_reactivated: number;
  voters_removed: number;
  rejected_rows_count: number;
  error_message: string | null;
}

export interface NCSBESyncRunsResponse {
  runs: NCSBESyncRun[];
}

export async function getNCSBESyncRuns(): Promise<NCSBESyncRunsResponse> {
  return apiFetchJson<NCSBESyncRunsResponse>('/api/admin/ncsbe-sync-runs');
}

// ── Admin: invites ───────────────────────────────────────────────────────────

export interface AdminInvite {
  id: string;
  token_masked: string;
  role: 'admin' | 'volunteer';
  max_uses: number;
  uses_count: number;
  expires_at: string;
  note: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface AdminInviteCreateBody {
  expires_in: string;
  max_uses: number;
  role: 'admin' | 'volunteer';
  note?: string | null;
}

export interface AdminInviteCreateResult {
  invite: AdminInvite;
  signup_url: string;
}

export async function adminCreateInvite(
  body: AdminInviteCreateBody,
): Promise<AdminInviteCreateResult> {
  return apiFetchJson<AdminInviteCreateResult>('/api/admin/invites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function adminListInvites(): Promise<AdminInvite[]> {
  return apiFetchJson<AdminInvite[]>('/api/admin/invites');
}

export async function adminRevokeInvite(id: string): Promise<AdminInvite> {
  return apiFetchJson<AdminInvite>(`/api/admin/invites/${id}/revoke`, {
    method: 'POST',
  });
}

// ── Admin: users ─────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  username: string;
  display_name: string | null;
  email: string | null;
  role: 'admin' | 'volunteer';
  disabled_at: string | null;
  created_at: string;
  created_via_invite_id: string | null;
  last_login_at: string | null;
}

export async function adminListUsers(): Promise<AdminUser[]> {
  return apiFetchJson<AdminUser[]>('/api/admin/users');
}

export async function adminDisableUser(id: string): Promise<AdminUser> {
  return apiFetchJson<AdminUser>(`/api/admin/users/${id}/disable`, { method: 'POST' });
}

export async function adminEnableUser(id: string): Promise<AdminUser> {
  return apiFetchJson<AdminUser>(`/api/admin/users/${id}/enable`, { method: 'POST' });
}

export async function adminPromoteUser(id: string): Promise<AdminUser> {
  return apiFetchJson<AdminUser>(`/api/admin/users/${id}/promote`, { method: 'POST' });
}

export async function adminDemoteUser(id: string): Promise<AdminUser> {
  return apiFetchJson<AdminUser>(`/api/admin/users/${id}/demote`, { method: 'POST' });
}

export interface AdminDeleteUserResult {
  id: string;
  deleted_username: string;
  status: string;
}

export async function adminDeleteUser(
  id: string,
  confirmUsername: string,
): Promise<AdminDeleteUserResult> {
  return apiFetchJson<AdminDeleteUserResult>(`/api/admin/users/${id}/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm_username: confirmUsername }),
  });
}

export interface AdminResetPasswordResult {
  reset_url: string;
  expires_at: string;
}

export async function adminResetUserPassword(
  id: string,
  expiresIn: string = '24h',
): Promise<AdminResetPasswordResult> {
  return apiFetchJson<AdminResetPasswordResult>(
    `/api/admin/users/${id}/reset-password`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expires_in: expiresIn }),
    },
  );
}

// ── Admin: sync now ──────────────────────────────────────────────────────────

export interface AdminSyncNowResult {
  status: string;
  voters_added: number;
  voters_updated: number;
  voters_removed: number;
  rejected_rows_count: number;
  error_message: string | null;
}

export async function adminSyncNow(): Promise<AdminSyncNowResult> {
  return apiFetchJson<AdminSyncNowResult>('/api/admin/sync-now', { method: 'POST' });
}

// ── /api/health for FreshnessChip ────────────────────────────────────────────

export interface HealthLastSync {
  at: string | null;
  status: string;
  age_minutes: number | null;
}

export interface HealthResponse {
  status: string;
  last_sync: HealthLastSync;
  geocode_queue: number;
}

export async function getHealth(): Promise<HealthResponse> {
  const res = await fetch('/api/health', { credentials: 'include' });
  if (!res.ok) throw new Error(`health check failed: ${res.status}`);
  return res.json() as Promise<HealthResponse>;
}

// ── Walk list ─────────────────────────────────────────────────────────────────

export interface WalkListItem {
  id: string;
  voter_reg_num: string;
  first_name: string | null;
  last_name: string | null;
  party: string | null;
  age: number | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  latest_support_level: number | null;
  latest_contact_result: string | null;
  latest_contact_at: string | null;
  do_not_contact: boolean;
  petition_signed: boolean | null;
  has_donated: boolean | null;
  has_volunteered: boolean | null;
  has_voted?: boolean | null;
}

export interface WalkListResponse {
  items: WalkListItem[];
  total: number;
  truncated: boolean;
}

export async function getWalkList(
  params: URLSearchParams,
  polygon?: PolygonVertex[] | null,
): Promise<WalkListResponse> {
  const p = new URLSearchParams(params);  // clone, don't mutate caller's params
  if (polygon && polygon.length >= 3) {
    p.set('polygon', polygon.map(v => `${v.lng},${v.lat}`).join(';'));
  }
  const qs = p.toString();
  return apiFetchJson<WalkListResponse>(`/api/voters/walk-list${qs ? '?' + qs : ''}`);
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export interface StatsCounts { total: number; spoke: number; supporters_id: number; }
export interface MyStatsResponse { all_time: StatsCounts; this_week: StatsCounts; week_starts_at: string; }
export interface LeaderboardEntry { user_id: string; display_name: string; username: string; total: number; }
export interface LeaderboardResponse { this_week: LeaderboardEntry[]; all_time: LeaderboardEntry[]; week_starts_at: string; }

export async function getMyStats(): Promise<MyStatsResponse> {
  return apiFetchJson<MyStatsResponse>('/api/me/stats');
}
export async function getLeaderboard(): Promise<LeaderboardResponse> {
  return apiFetchJson<LeaderboardResponse>('/api/stats/leaderboard');
}

// ── Admin: consultant deliveries ─────────────────────────────────────────────

export interface ConsultantDelivery {
  id: string;
  source: 'drive' | 'upload' | 'cli';
  source_ref: string | null;
  filename: string;
  sha256: string;
  received_at: string;
  imported_at: string | null;
  status: 'running' | 'ok' | 'error';
  row_count: number;
  skipped_count: number;
  matched_count: number;
  unmatched_count: number;
  phones_filled: number;
  error_message: string | null;
  imported_by_user_id: string | null;
}

export interface ConsultantDeliveryList {
  items: ConsultantDelivery[];
  drive_configured: boolean;
}

export interface DriveCheckResult {
  imported: number;
  skipped: number;
  errors: number;
}

export async function adminListDeliveries(): Promise<ConsultantDeliveryList> {
  return apiFetchJson<ConsultantDeliveryList>('/api/admin/consultant/deliveries');
}

export async function adminUploadDelivery(file: File): Promise<{ id: string }> {
  const form = new FormData();
  form.append('file', file);
  // Deliberately no Content-Type header: the browser has to set the multipart
  // boundary itself.
  return apiFetchJson<{ id: string }>('/api/admin/consultant/deliveries', {
    method: 'POST',
    body: form,
  });
}

export async function adminDriveCheck(): Promise<DriveCheckResult> {
  return apiFetchJson<DriveCheckResult>('/api/admin/consultant/drive-check', {
    method: 'POST',
  });
}

// ── Admin: early-vote ballot imports ─────────────────────────────────────────

export interface BallotImport {
  id: string;
  source: 'ncsbe' | 'upload' | 'cli';
  filename: string;
  sha256: string;
  file_last_modified: string | null;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'ok' | 'error';
  row_count: number;
  matched_count: number;
  voted_count: number;
  error_message: string | null;
  imported_by_user_id: string | null;
}

export interface BallotUploadResult {
  /** Present when the import was queued (202). */
  id?: string;
  /** 'unchanged' when the file is byte-identical to the latest ok import (200). */
  status?: 'unchanged';
}

export interface BallotSyncResult {
  status: 'ok' | 'unchanged' | 'error';
  rows?: number;
  matched?: number;
  voted?: number;
  error?: string;
}

export async function adminListBallotImports(): Promise<{ items: BallotImport[] }> {
  return apiFetchJson<{ items: BallotImport[] }>('/api/admin/ballots/imports');
}

export async function adminUploadBallotFile(file: File): Promise<BallotUploadResult> {
  const form = new FormData();
  form.append('file', file);
  return apiFetchJson<BallotUploadResult>('/api/admin/ballots/imports', {
    method: 'POST',
    body: form,
  });
}

export async function adminBallotSyncNow(): Promise<BallotSyncResult> {
  return apiFetchJson<BallotSyncResult>('/api/admin/ballots/sync-now', { method: 'POST' });
}

// ── Admin: dashboards (/api/admin/dashboards) ────────────────────────────────

export interface DeliveryStamp {
  id: string;
  filename: string;
  received_at: string;
  row_count: number;
}

export interface PropensityPartyResponse {
  delivery: DeliveryStamp | null;
  rows: { propensity_bucket: string; party_preference: string; count: number }[];
}

export interface OutreachPrecinctResponse {
  delivery: DeliveryStamp | null;
  rows: { precinct: string; state: string; count: number }[];
}

export interface ScorecardRow {
  precinct: string;
  registered: number;
  active: number;
  on_target_list: number;
  text_contacted: number;
  opted_in: number;
  door_contacts: number;
  spoke: number;
  avg_support: number | null;
}

export interface PrecinctScorecardResponse {
  delivery: DeliveryStamp | null;
  rows: ScorecardRow[];
}

export interface PreferenceAgeResponse {
  delivery: DeliveryStamp | null;
  rows: { age_range: string; voting_preference: string; count: number }[];
}

export interface WriteInIssue {
  contact_id: string;
  voter_id: string;
  voter_name: string;
  text: string;
  created_at: string;
}

export interface ActivityResponse {
  weeks: { week_start: string; results: Record<string, number> }[];
  issues: { tag: string; display_name: string; count: number }[];
  // Optional: a response cached from before write-ins existed has no such key,
  // and the page falls back to the empty table rather than throwing.
  write_ins?: { total: number; rows: WriteInIssue[] };
}

export async function getPropensityParty(): Promise<PropensityPartyResponse> {
  return apiFetchJson<PropensityPartyResponse>('/api/admin/dashboards/propensity-party');
}

export async function getOutreachPrecinct(): Promise<OutreachPrecinctResponse> {
  return apiFetchJson<OutreachPrecinctResponse>('/api/admin/dashboards/outreach-precinct');
}

export async function getPrecinctScorecard(): Promise<PrecinctScorecardResponse> {
  return apiFetchJson<PrecinctScorecardResponse>('/api/admin/dashboards/precinct-scorecard');
}

export async function getPreferenceAge(): Promise<PreferenceAgeResponse> {
  return apiFetchJson<PreferenceAgeResponse>('/api/admin/dashboards/preference-age');
}

export async function getActivity(): Promise<ActivityResponse> {
  return apiFetchJson<ActivityResponse>('/api/admin/dashboards/activity');
}
